import {
  exampleCancellationHours,
  formatAED,
  isMembership,
  MEMBERSHIP_RENEWAL_HELP,
  membershipOfferLine,
  NEW_CLIENTS_ONLY_HELP,
  newClientsOnlyLabel,
  simulateCancellations,
  type BusinessRules,
  type ClassKind,
  type FreeCancellationMode,
  type Group,
  type PackageKind,
  type PackageProduct,
  type Policy,
} from "@teamna/shared";
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  RowMenu,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  Notice,
  OwnerListRow,
  Panel,
  PlusCircleIcon,
  RadioGroup,
  SkeletonList,
  ToggleRow,
  showToast,
  type DataTableColumn,
} from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import {
  useActiveBusiness,
  useCreatePackageProduct,
  useOwnerGroups,
  useOwnerPackageProducts,
  useUpdatePackageProduct,
} from "@teamna/web-client/hooks/use-owner.ts";
import { useSaveRules } from "@teamna/web-client/hooks/use-owner-settings.ts";
import { usePackagesAndRules } from "@teamna/web-client/hooks/use-setup.ts";
import { OfficePage, runsBusiness } from "./office.tsx";
import { RulesSection } from "./setup/packages.tsx";
import { FeeRulesSection } from "../fees/fee-rules.tsx";
import { WaitlistRuleCard } from "./waitlist-rule.tsx";
import { CarryOverRuleCard } from "./carry-over-rule.tsx";
import { PackageShopList } from "../member/package-shop.tsx";

/**
 * What the business sells, and on what terms (web-app-v2.md slice E).
 *
 * The register of packages at the top (name, kind, sessions, price, validity, renewal, on sale or
 * not), each opening in a Panel to edit; below it the business-wide cancellation rule with the
 * sentence members read, saved on its own. The setup step saves the whole package list in one go;
 * this page changes one package at a time, which is what an owner does after the first day.
 *
 * Editing a package never touches a package already sold. Every purchase snapshots its own credits,
 * price and cancellation allowance at checkout, so the Panel says so. "Stop selling" rather than
 * deleting, because a package with purchases behind it is a record and the API refuses to delete one.
 *
 * Packages and prices are the owner's alone (the API refuses admins); the rule is open to an admin.
 * So an admin sees no "Add a package" button.
 */
export function OwnerPackagesScreen() {
  const [editing, setEditing] = useState<PackageProduct | null>(null);
  const [creating, setCreating] = useState<false | PackageKind>(false);
  const [copying, setCopying] = useState<PackageProduct | null>(null);
  const isOwner = useActiveBusiness().business?.staffRole === "owner";

  return (
    <OfficePage
      title="Packages and rules"
      description="What you sell, and what happens when someone cancels."
      actions={
        isOwner ? (
          <Button onClick={() => setCreating("pack")}>
            <PlusCircleIcon size={18} />
            Add a package
          </Button>
        ) : undefined
      }
    >
      {(business) => (
        <PackagesBody
          businessId={business.businessId}
          isOwner={business.staffRole === "owner"}
          runs={runsBusiness(business)}
          editing={editing}
          creating={creating}
          copying={copying}
          setEditing={setEditing}
          setCreating={setCreating}
          setCopying={setCopying}
        />
      )}
    </OfficePage>
  );
}

function PackagesBody({
  businessId,
  isOwner,
  runs,
  editing,
  creating,
  copying,
  setEditing,
  setCreating,
  setCopying,
}: {
  businessId: string;
  isOwner: boolean;
  runs: boolean;
  editing: PackageProduct | null;
  /** The kind a new package starts as, or false when no new package is open. */
  creating: false | PackageKind;
  setEditing: (product: PackageProduct | null) => void;
  setCreating: (open: false | PackageKind) => void;
  /** The package a new one is copied from (Duplicate in the row's More menu), or null. */
  copying: PackageProduct | null;
  setCopying: (product: PackageProduct | null) => void;
}) {
  const { label } = useLabels();
  const { shape, ready } = useShape(businessId);
  const products = useOwnerPackageProducts(businessId, isOwner);
  const groups = useOwnerGroups(businessId);
  const rows = products.data ?? [];
  const groupWord = label("group", { capital: true });

  const newOnlyWord = newClientsOnlyLabel(label("member", { plural: true }));
  const columns = useMemo(
    () => packageColumns(groupWord, newOnlyWord, setCopying),
    [groupWord, newOnlyWord, setCopying],
  );

  return (
    <div className="flex flex-col gap-8">
      {!isOwner ? (
        <EmptyState
          title="Owner only"
          message="Packages and prices are set by the business owner. Ask them if something needs changing."
        />
      ) : products.error && !products.data ? (
        <ErrorState error={products.error} onRetry={() => void products.refetch()} />
      ) : (
        <DataTable
          caption="Packages"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowLabel={(row) => row.name}
          onRowClick={(row) => setEditing(row)}
          isLoading={products.isLoading}
          defaultSort={{ id: "active", direction: "asc" }}
          countLabel={(count) => `${count} ${count === 1 ? "package" : "packages"}`}
          empty={{
            title: "No packages yet",
            message: `A package is a block of sessions a ${label("member")} buys up front. A business on weekly slots and monthly fees does not need one.`,
            action: (
              <Button variant="secondary" onClick={() => setCreating("pack")}>
                Add a package
              </Button>
            ),
          }}
        />
      )}

      {/* A starting example (M2): where clients book for themselves, a monthly membership is a usual way to sell. */}
      {isOwner && ready && shape.bookingFor === "self" && products.data && !rows.some((row) => isMembership(row)) ? (
        <Notice
          tone="info"
          title="Sell a monthly membership"
          action={
            <Button variant="secondary" onClick={() => setCreating("membership")}>
              Add a membership
            </Button>
          }
        >
          For example 8 sessions a month, or unlimited, for a price each month. Teamna asks for the payment each month and sends reminders.
        </Notice>
      ) : null}

      {runs ? <RulesBlock businessId={businessId} /> : null}
      {runs ? <WaitlistRuleCard businessId={businessId} /> : null}
      {runs ? <CarryOverRuleCard businessId={businessId} /> : null}

      {isOwner && creating ? (
        <PackagePanel
          businessId={businessId}
          groups={groups.data ?? []}
          startAs={creating}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {isOwner && copying ? (
        <PackagePanel
          key={`copy-${copying.id}`}
          businessId={businessId}
          copyOf={copying}
          groups={groups.data ?? []}
          onClose={() => setCopying(null)}
        />
      ) : null}

      {isOwner && editing ? (
        <PackagePanel
          key={editing.id}
          businessId={businessId}
          product={editing}
          groups={groups.data ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The package register's columns. `groupWord` is the business's word for a group, "Level";
 * `newOnlyWord` names an intro offer, "New clients only". The last column is the row's More menu,
 * with Duplicate: a new package that starts as a copy of this one.
 */
function packageColumns(
  groupWord: string,
  newOnlyWord: string,
  onDuplicate: (product: PackageProduct) => void,
): DataTableColumn<PackageProduct>[] {
  return [
      {
        id: "name",
        header: "Name",
        sortValue: (row) => row.name,
        cell: (row) => (
          <div className="min-w-[160px]">
            <p className={row.active ? "font-semibold text-ink" : "font-semibold text-ink-3"}>{row.name}</p>
            <p className="text-[12px] text-ink-3">
              {cancellationLabel(row.freeCancellations, row.freeCancellationMode)}
            </p>
            {row.newClientsOnly ? (
              <span className="mt-1 inline-flex">
                <Badge tone="late">{newOnlyWord}</Badge>
              </span>
            ) : null}
            {isMembership(row) ? (
              <span className="mt-1 inline-flex">
                <Badge tone="info">Membership</Badge>
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: "kind",
        header: "Pays for",
        hideOnPhone: true,
        sortValue: (row) => row.kind,
        cell: (row) => kindLabel(row.kind, groupWord),
      },
      {
        id: "sessions",
        header: "Sessions",
        align: "end",
        sortValue: (row) => (row.unlimited ? Number.MAX_SAFE_INTEGER : row.credits),
        cell: (row) => <span className="tabular-nums">{sessionsLabel(row)}</span>,
      },
      {
        id: "price",
        header: "Price",
        align: "end",
        sortValue: (row) => row.priceFils,
        cell: (row) => (
          <div className="whitespace-nowrap">
            <p className="font-semibold tabular-nums">{formatAED(row.priceFils)}</p>
            <p className="text-[12px] text-ink-3 tabular-nums">{perSessionLabel(row)}</p>
          </div>
        ),
      },
      {
        id: "validity",
        header: "Valid for",
        hideOnPhone: true,
        sortValue: (row) => row.validityDays,
        cell: (row) => (isMembership(row) ? `Every ${row.validityDays} days` : `${row.validityDays} days`),
      },
      {
        id: "active",
        header: "Status",
        sortValue: (row) => (row.active ? 0 : 1),
        cell: (row) =>
          row.active ? <Badge tone="confirmed">On sale</Badge> : <Badge tone="quiet">Not on sale</Badge>,
      },
      {
        id: "actions",
        header: "",
        align: "end",
        width: "56px",
        cell: (row) => (
          <RowMenu label={`More for ${row.name}`} items={[{ label: "Duplicate", onSelect: () => onDuplicate(row) }]} />
        ),
      },
    ];
}

/** The rule block, read from the setup packages endpoint and saved on its own. */
function RulesBlock({ businessId }: { businessId: string }) {
  const current = usePackagesAndRules(businessId);
  const save = useSaveRules(businessId);
  const [edited, setEdited] = useState<BusinessRules | null>(null);
  // A typed fee that is not an amount yet: the rule does not save until it is (I2).
  const [feeInvalid, setFeeInvalid] = useState(false);
  const rules = edited ?? current.data?.rules ?? null;

  if (current.isLoading) return <SkeletonList rows={2} />;
  if (current.error || !rules) {
    return (
      <ErrorState
        error={current.error ?? new Error("missing")}
        onRetry={() => void current.refetch()}
      />
    );
  }

  const changed = edited !== null && JSON.stringify(edited) !== JSON.stringify(current.data?.rules);

  return (
    <div className="flex flex-col gap-3">
      <RulesSection rules={rules} onChange={setEdited} />
      <FeeRulesSection
        rules={rules}
        onChange={setEdited}
        onInvalid={setFeeInvalid}
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              disabled={!changed || feeInvalid}
              loading={save.isPending}
              onClick={() =>
                save.mutate(rules, {
                  onSuccess: () => {
                    setEdited(null);
                    showToast("Rules saved");
                  },
                })
              }
            >
              Save the rules
            </Button>
            <p className="text-[13px] text-ink-3">
              The new rules apply to cancellations and marks from now on.
            </p>
          </div>
        }
      />
      {save.error ? <InlineError error={save.error} /> : null}
    </div>
  );
}

/**
 * What a package pays for, in the business's word for a group: "Level sessions", "Class sessions",
 * or "Private sessions". "sessions", not "classes": a pilates studio calls a group a class.
 */
export function kindLabel(kind: ClassKind, groupWord: string): string {
  return kind === "private" ? "Private sessions" : `${groupWord} sessions`;
}

/** "AED 90 each", "AED 600 a month". */
function perSessionLabel(product: PackageProduct): string {
  if (isMembership(product)) return product.unlimited ? "a month" : `a month, ${formatAED(Math.round(product.priceFils / Math.max(1, product.credits)))} a session`;
  return `${formatAED(Math.round(product.priceFils / Math.max(1, product.credits)))} each`;
}

/** "10", "8 a month", "Unlimited". */
function sessionsLabel(product: PackageProduct): string {
  if (!isMembership(product)) return String(product.credits);
  return product.unlimited ? "Unlimited" : `${product.credits} a month`;
}

/** The one line under the name: what you get, for how long, and on what cancellation terms. */
export function subtitleFor(product: PackageProduct): string {
  if (isMembership(product)) {
    return [
      product.unlimited ? "Unlimited, each month" : `${product.credits} sessions a month`,
      perSessionLabel(product),
      cancellationLabel(product.freeCancellations, product.freeCancellationMode),
    ].join(" · ");
  }
  return [
    `${product.credits} sessions`,
    `valid ${product.validityDays} days`,
    perSessionLabel(product),
    cancellationLabel(product.freeCancellations, product.freeCancellationMode),
  ].join(" · ");
}

/**
 * What the allowance covers, in the words of the mode. On `any_cancel` it counts the cancellations
 * before the cut-off, and saying so keeps this line from reading as a promise that sits against the
 * cut-off rule ("cancel any time" next to "12 hours before"). On `late_only` it counts late ones.
 */
export function cancellationLabel(
  freeCancellations: number | null,
  mode: FreeCancellationMode = "any_cancel",
): string {
  if (freeCancellations === null) return "no limit on cancellations before the cut-off";
  if (freeCancellations === 0) return "no free cancellations";
  const plural = freeCancellations === 1 ? "cancellation" : "cancellations";
  if (mode === "late_only") return `${freeCancellations} free late ${plural}`;
  return `${freeCancellations} free ${plural} before the cut-off`;
}

type Draft = {
  name: string;
  /** A block of sessions, or a monthly membership with a cap (M2). Set when the package is added. */
  packageKind: PackageKind;
  /** A membership with no cap. */
  unlimited: boolean;
  kind: ClassKind;
  credits: string;
  price: string;
  validityDays: string;
  newClientsOnly: boolean;
  freeCancellations: string;
  freeCancellationMode: FreeCancellationMode;
  groupIds: string[] | null;
};

/**
 * Add or edit one package, in a Panel.
 *
 * The two are the same fields and the same checks, and the only difference is which mutation runs
 * at the end. Money is typed in whole dirhams and sent as fils: an owner thinks in dirhams, the API
 * stores the minor unit, and the conversion belongs at that boundary.
 *
 * "New clients only" makes the package an intro offer (migration 0070): only a person with no
 * earlier purchase and no attended session here can buy it, one time, and the shop shows it first.
 * A block of sessions never renews by itself: the "Renews each month" switch is gone (2026-10-30),
 * because it renewed nothing and read like a membership. A monthly plan is a membership.
 *
 * A monthly membership (M2) is chosen when the package is added, never changed after: classes a
 * month (or unlimited) and a price a month. Each month runs its "Days in each month" from its first
 * day, then it renews by a payment request, so it has no renewal switch and no intro offer.
 *
 * Duplicate (`copyOf`) is an add that starts with every field of another package and its name plus
 * "(copy)". Nothing is saved until the owner puts it on sale, so a copy can be changed first.
 */
export function PackagePanel({
  businessId,
  product,
  copyOf,
  groups,
  startAs = "pack",
  onClose,
}: {
  businessId: string;
  product?: PackageProduct;
  /** Add a new package that starts as a copy of this one. */
  copyOf?: PackageProduct;
  groups: Group[];
  /** What a new package starts as: the membership example opens on `membership`. */
  startAs?: PackageKind;
  onClose: () => void;
}) {
  const { label } = useLabels();
  const isEdit = product !== undefined;
  const create = useCreatePackageProduct(businessId);
  const update = useUpdatePackageProduct(businessId);
  const rules = usePackagesAndRules(businessId).data?.rules ?? null;

  const [draft, setDraft] = useState<Draft>(() => {
    const from = product ?? copyOf;
    return {
      name: copyOf ? `${copyOf.name} (copy)` : (product?.name ?? (startAs === "membership" ? "8 sessions a month" : "")),
      packageKind: from?.packageKind ?? startAs,
      unlimited: from?.unlimited ?? false,
      kind: from?.kind ?? "group",
      credits: from ? String(from.credits) : startAs === "membership" ? "8" : "",
      price: from ? String(Math.round(from.priceFils / 100)) : "",
      validityDays: from ? String(from.validityDays) : "30",
      newClientsOnly: from?.newClientsOnly ?? false,
      freeCancellations: from?.freeCancellations === null ? "" : String(from?.freeCancellations ?? ""),
      // A new package starts on late_only: early cancels stay free, which members find fair.
      freeCancellationMode: from?.freeCancellationMode ?? "late_only",
      groupIds: from?.groupIds ?? null,
    };
  });
  const [error, setError] = useState<unknown>(null);

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));
  const digits = (value: string) => value.replace(/[^0-9]/g, "");

  const membership = draft.packageKind === "membership";
  const unlimited = membership && draft.unlimited;
  const credits = unlimited ? 0 : Number(draft.credits);
  const priceFils = Number(draft.price) * 100;
  // A membership month runs from one renewal day to the next; the API keeps 31 days as its validity.
  const validityDays = Number(draft.validityDays);
  const perSession =
    Number.isFinite(priceFils) && credits > 0 && draft.price !== "" ? Math.round(priceFils / credits) : null;

  const canSubmit =
    draft.name.trim().length > 0 &&
    Number.isInteger(credits) &&
    (unlimited || credits >= 1) &&
    Number.isFinite(priceFils) &&
    priceFils >= 0 &&
    Number.isInteger(validityDays) &&
    validityDays >= 1;

  const allowance = draft.freeCancellations.trim() === "" ? null : Number(draft.freeCancellations);
  // Zero free cancellations means "never free", which only any_cancel can say. On late_only zero
  // would mean the same as no limit, and the owner did not type a zero for that.
  const mode: FreeCancellationMode = allowance === 0 ? "any_cancel" : draft.freeCancellationMode;
  const groupPlural = label("group", { plural: true });

  // The package as the shop will list it, from what is typed so far.
  const preview: PackageProduct = {
    id: product?.id ?? "00000000-0000-4000-8000-000000000000",
    businessId,
    name: draft.name.trim() || "Your package name",
    credits: Number.isInteger(credits) ? credits : 0,
    priceFils: Number.isFinite(priceFils) ? priceFils : 0,
    validityDays: Number.isInteger(validityDays) ? validityDays : 0,
    groupIds: draft.groupIds,
    freeCancellations: allowance,
    freeCancellationMode: mode,
    active: true,
    kind: draft.kind,
    autoRenew: membership,
    newClientsOnly: membership ? false : draft.newClientsOnly,
    packageKind: draft.packageKind,
    unlimited,
    createdAt: product?.createdAt ?? "2026-01-01T00:00:00.000Z",
    updatedAt: product?.updatedAt ?? "2026-01-01T00:00:00.000Z",
  };

  async function submit(): Promise<void> {
    setError(null);
    const body = {
      name: draft.name.trim(),
      kind: draft.kind,
      credits,
      priceFils,
      validityDays,
      autoRenew: membership,
      newClientsOnly: membership ? false : draft.newClientsOnly,
      groupIds: draft.groupIds,
      freeCancellations: allowance,
      freeCancellationMode: mode,
      unlimited,
    };
    try {
      if (isEdit) {
        await update.mutateAsync({ productId: product.id, patch: body });
        showToast(`${body.name} saved. Past buyers keep their terms.`);
      } else {
        await create.mutateAsync({
          ...body,
          packageKind: draft.packageKind,
          ...(copyOf ? { copiedFrom: copyOf.id } : {}),
        });
        showToast(`${body.name} is on sale at ${formatAED(priceFils)}`);
      }
      onClose();
    } catch (caught) {
      setError(caught);
    }
  }

  async function toggleHidden(): Promise<void> {
    if (!product) return;
    setError(null);
    try {
      await update.mutateAsync({ productId: product.id, patch: { active: !product.active } });
      showToast(
        product.active
          ? `${product.name} is no longer on sale. ${label("member", { plural: true, capital: true })} who bought it keep their sessions.`
          : `${product.name} is on sale again`,
      );
      onClose();
    } catch (caught) {
      setError(caught);
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Panel
      open
      onClose={onClose}
      placement="center"
      width="xl"
      title={isEdit ? product.name : copyOf ? "Duplicate a package" : "Add a package"}
      subtitle={
        copyOf
          ? `A copy of ${copyOf.name}. Change what you need, then put it on sale.`
          : isEdit
          ? membership
            ? "Changes apply from the next month"
            : "Changes apply to new purchases only"
          : membership
            ? "A monthly membership, renewed each month"
            : "A block of sessions, bought up front"
      }
      actions={
        <>
          <Button disabled={!canSubmit} loading={pending} onClick={() => void submit()}>
            {isEdit ? "Save changes" : "Put it on sale"}
          </Button>
          {isEdit ? (
            <Button variant="secondary" onClick={() => void toggleHidden()}>
              {product.active ? "Stop selling" : "Put back on sale"}
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          )}
        </>
      }
    >
      {error ? <InlineError error={error} /> : null}

      {/* Two columns at desktop width: every control on the left, what parents will see on the right. */}
      <div className="grid gap-x-8 gap-y-4 pb-2 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-4">
          <Field
            label="Name"
            value={draft.name}
            onChange={(event) => set({ name: event.target.value })}
            placeholder="Starter pack"
            maxLength={120}
            hint={`What a ${label("member")} sees on the packages list.`}
          />

          {isEdit ? null : (
            <RadioGroup<PackageKind>
              legend="What it is"
              options={[
                { value: "pack", label: "Block of sessions", hint: "A number of sessions, bought up front, used before a date." },
                {
                  value: "membership",
                  label: "Monthly membership",
                  hint: "A number of sessions each month, or unlimited, for a price each month. Sessions not used in a month do not carry over.",
                },
              ]}
              value={draft.packageKind}
              onChange={(packageKind) => set({ packageKind })}
            />
          )}

          <RadioGroup<ClassKind>
            legend="Pays for"
            options={[
              {
                value: "group",
                label: kindLabel("group", label("group", { capital: true })),
                hint: `Many ${label("participant", { plural: true })} share each class.`,
              },
              {
                value: "private",
                label: kindLabel("private", label("group", { capital: true })),
                hint: `The private slots in your schedule. One ${label("participant")} in each slot.`,
              },
            ]}
            value={draft.kind}
            onChange={(kind) => set({ kind })}
          />

          {membership ? (
            <ToggleRow
              label="Unlimited"
              hint={`No limit on the sessions a ${label("member")} books each month.`}
              checked={draft.unlimited}
              onChange={(next) => set({ unlimited: next })}
            />
          ) : null}

          <div className="flex gap-3">
            {unlimited ? null : (
            <div className="flex-1">
              <Field
                label={membership ? "Sessions a month" : "Sessions"}
                value={draft.credits}
                onChange={(event) => set({ credits: digits(event.target.value) })}
                inputMode="numeric"
                maxLength={4}
                placeholder={membership ? "8" : "10"}
              />
            </div>
            )}
            <div className="flex-1">
              <Field
                label={membership ? "Price a month, AED" : "Price, AED"}
                value={draft.price}
                onChange={(event) => set({ price: digits(event.target.value) })}
                inputMode="numeric"
                maxLength={7}
                placeholder="900"
              />
            </div>
          </div>

          {perSession !== null ? (
            <div className="rounded-card bg-accent-surface px-4 py-3 text-center">
              <p className="eyebrow text-accent-strong">Works out at</p>
              <p className="text-[24px] font-semibold text-accent-strong">
                {formatAED(perSession)}
              </p>
              <p className="mt-0.5 text-[13px] text-ink-2">per session</p>
            </div>
          ) : null}

          {membership ? (
            <>
              <Field
                label="Days in each month"
                value={draft.validityDays}
                onChange={(event) => set({ validityDays: digits(event.target.value) })}
                inputMode="numeric"
                maxLength={3}
                placeholder="30"
                hint="A month starts on the day they buy and runs this many days."
              />
              <Notice tone="info" title={membershipOfferLine({ cap: unlimited ? null : credits > 0 ? credits : null, days: validityDays > 0 ? validityDays : 30 })}>
                {MEMBERSHIP_RENEWAL_HELP} Each month costs the full price.
              </Notice>
            </>
          ) : (
            <Field
              label="Valid for, days"
              value={draft.validityDays}
              onChange={(event) => set({ validityDays: digits(event.target.value) })}
              inputMode="numeric"
              maxLength={3}
              placeholder="60"
              hint={`How long a ${label("member")} has to use the sessions after buying.`}
            />
          )}

          {membership ? null : (
            <ToggleRow
              label={newClientsOnlyLabel(label("member", { plural: true }))}
              hint={NEW_CLIENTS_ONLY_HELP}
              checked={draft.newClientsOnly}
              onChange={(newClientsOnly) => set({ newClientsOnly })}
            />
          )}


          <Field
            label="Free cancellations"
            value={draft.freeCancellations}
            onChange={(event) => set({ freeCancellations: digits(event.target.value) })}
            inputMode="numeric"
            maxLength={4}
            placeholder="Any number"
            hint={allowanceHint(allowance, mode, label("member"))}
          />

          {allowance !== null && allowance > 0 ? (
            <div>
              <p className="eyebrow pb-2 text-ink-2">Which cancellations use them</p>
              <div className="overflow-hidden rounded-card border border-line">
                <OwnerListRow
                  title="Only late cancellations"
                  subtitle="Early cancellations are always free"
                  onSelect={() => set({ freeCancellationMode: "late_only" })}
                  trailing={mode === "late_only" ? <Badge tone="confirmed">Picked</Badge> : undefined}
                />
                <OwnerListRow
                  title="Every cancellation"
                  subtitle="Early and late cancellations both use one"
                  onSelect={() => set({ freeCancellationMode: "any_cancel" })}
                  trailing={mode === "any_cancel" ? <Badge tone="confirmed">Picked</Badge> : undefined}
                  last
                />
              </div>
              {mode === "late_only" && rules?.lateCancelRule === "session_returned" ? (
                <div className="mt-3">
                  <Notice tone="info" title="Your rule already gives late cancellations back">
                    Free late cancellations change nothing while your cancellation rule says "The
                    session goes back". Change the rule on this page to "The session is lost" to use them.
                  </Notice>
                </div>
              ) : null}
            </div>
          ) : null}

          {allowance !== null && rules ? (
            <CancellationExample rules={rules} allowance={allowance} mode={mode} />
          ) : null}

          <div>
            <p className="eyebrow pb-2 text-ink-2">Covers</p>
            <div className="overflow-hidden rounded-card border border-line">
              <OwnerListRow
                title={`Every ${label("group")}`}
                subtitle={`Sessions can be spent on any of your ${groupPlural}`}
                onSelect={() => set({ groupIds: null })}
                trailing={draft.groupIds === null ? <Badge tone="confirmed">Picked</Badge> : undefined}
                last={groups.length === 0}
              />
              {groups.map((group, index) => {
                const picked = draft.groupIds?.includes(group.id) ?? false;
                return (
                  <OwnerListRow
                    key={group.id}
                    title={group.name}
                    subtitle={`${group.durationMin} min · ${group.defaultCapacity === null ? "no limit on places" : `${group.defaultCapacity} spots`}`}
                    onSelect={() => {
                      const current = draft.groupIds ?? [];
                      const next = picked
                        ? current.filter((id) => id !== group.id)
                        : [...current, group.id];
                      set({ groupIds: next.length === 0 ? null : next });
                    }}
                    trailing={picked ? <Badge tone="confirmed">Picked</Badge> : undefined}
                    last={index === groups.length - 1}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <PackagePreview product={preview} />

        {isEdit ? (
          <p className="text-[13px] text-ink-3 lg:col-span-2">
            {membership
              ? "A month already paid keeps its price and its sessions. The next renewal requests use the new price and number."
              : "Everyone who already bought this keeps the price, the sessions and the cancellations they paid for. Changes here only apply to the next person who buys it."}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * What parents see (Add a package workshop, 2026-10-30): the package as their shop lists it, drawn by
 * the shop's own list (`PackageShopList`), so it never says something the shop does not. It follows
 * the form as the owner types: an intro offer gets its tag, a membership its badge and "a month".
 * `inert`: it is a picture of the shop, not a Buy button.
 */
function PackagePreview({ product }: { product: PackageProduct }) {
  const { label } = useLabels();
  const members = label("member", { plural: true });
  return (
    <div className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-0 lg:self-start">
      <p className="eyebrow text-ink-2">What {members} see</p>
      <div className="rounded-[28px] border border-line bg-sunken p-3 shadow-[inset_0_1px_2px_rgba(16,25,23,0.06)]">
        <div className="rounded-[20px] bg-paper px-4 pt-4 pb-2">
          <p className="pb-1 text-[17px] font-semibold text-ink">Packages</p>
          <div inert>
            <PackageShopList
              products={[product]}
              currency="AED"
              words={{ member: label("member"), groups: label("group", { plural: true }) }}
              disabled={false}
              onBuy={() => {}}
            />
          </div>
        </div>
      </div>
      <p className="text-[13px] text-ink-3">
        {product.newClientsOnly
          ? `Only new ${members} see it, at the top of their list. Each one can buy it one time.`
          : `This is how it shows on the Packages page of the ${label("member")} app.`}
      </p>
    </div>
  );
}

function allowanceHint(allowance: number | null, mode: FreeCancellationMode, member: string): string {
  if (allowance === null) {
    return "Leave blank for unlimited. Cancelling before your cut-off always returns the session.";
  }
  if (allowance === 0) {
    return `Every cancellation uses the session up, however early the ${member} cancels.`;
  }
  if (mode === "late_only") {
    return `Cancelling before your cut-off is always free. The first ${allowance} late ${
      allowance === 1 ? "cancellation returns its" : "cancellations return their"
    } session too. After that, your rule for late cancellations applies.`;
  }
  return `The first ${allowance} ${allowance === 1 ? "cancellation returns its" : "cancellations return their"} session. After that, cancelling uses the session up.`;
}

/** The business rule as the policy engine reads it, so the example runs the real engine. */
export function policyFromRules(rules: BusinessRules): Policy {
  return {
    cancelCutoffHours: rules.freeCancelCutoffHours,
    lateCancelRule: rules.lateCancelRule === "session_returned" ? "return_credit" : "lose_credit",
    lateCancelPartialBps: null,
    rescheduleLimitPerBooking: null,
    rescheduleLimitPerMonth: rules.rescheduleLimitPerMonth,
    minBookAheadMin: 0,
    maxBookAheadDays: 56,
  };
}

/** "3 days before", "5 hours before", "at the start time". */
function whenLabel(hours: number): string {
  if (hours === 0) return "at the start time";
  if (hours >= 24 && hours % 24 === 0) return `${hours / 24} ${hours === 24 ? "day" : "days"} before`;
  return `${hours} ${hours === 1 ? "hour" : "hours"} before`;
}

/**
 * A worked example under the allowance: one member, four cancellations, what happens to each.
 *
 * It runs the policy engine itself (`simulateCancellations`) with this package's numbers and the
 * business rule, so the owner reads exactly what members will get, and it changes as they type.
 * The four cancellations are two early, one late and one more early: the case where the two modes
 * give different answers.
 */
function CancellationExample({
  rules,
  allowance,
  mode,
}: {
  rules: BusinessRules;
  allowance: number;
  mode: FreeCancellationMode;
}) {
  const { label } = useLabels();
  const policy = policyFromRules(rules);
  const rows = simulateCancellations({
    policy,
    freeCancellations: allowance,
    mode,
    hoursBefore: exampleCancellationHours(policy.cancelCutoffHours),
  }).map((row, index) => Object.assign(row, { number: index + 1 }));
  const lost = rows.filter((row) => !row.sessionReturned).length;
  const member = label("member");

  return (
    <div data-testid="cancellation-example" className="rounded-card border border-line bg-surface p-4">
      <p className="eyebrow text-ink-2">Example</p>
      <p className="mt-1 text-[14px] text-ink-2">
        A {member} has this package and cancels 4 times. Your cut-off is{" "}
        {policy.cancelCutoffHours === 0 ? "the start time" : `${whenLabel(policy.cancelCutoffHours)} the session`}.
      </p>
      <ol className="mt-3 flex flex-col gap-2">
        {rows.map((row) => {
          const late = row.hoursBefore < policy.cancelCutoffHours;
          return (
            <li
              key={row.number}
              className="grid grid-cols-[1fr_auto] items-center gap-x-3 rounded-lg bg-paper px-3 py-2"
            >
              <span className="text-[14px] text-ink">
                {row.number}. Cancels {whenLabel(row.hoursBefore)}
                <span className="ml-1.5 text-[12px] text-ink-3">{late ? "late" : "early"}</span>
              </span>
              <span
                className={`text-[14px] font-semibold ${row.sessionReturned ? "text-confirmed" : "text-late"}`}
              >
                {row.sessionReturned ? "Session goes back" : "Session is lost"}
              </span>
              <span className="col-span-2 text-[12px] text-ink-3">
                {row.spent ? `Uses 1 free ${mode === "late_only" ? "late " : ""}cancellation. ` : ""}
                {row.freeLeft} free {mode === "late_only" ? "late " : ""}
                {row.freeLeft === 1 ? "cancellation" : "cancellations"} left
              </span>
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-[14px] font-semibold text-ink" data-testid="cancellation-example-result">
        {lost === 0
          ? `The ${member} loses no sessions.`
          : `The ${member} loses ${lost} ${lost === 1 ? "session" : "sessions"}.`}
      </p>
    </div>
  );
}
